import React from 'react';
import {useSiteMetadata} from '../hooks/use-site-metadata';

/* eslint-disable complexity, max-statements, sort-keys-fix/sort-keys-fix */

type Metadata = {
  content: string;
  name?: string;
  property?: string;
};

/**
 * mergeMetadata
 *
 * @param {Array} base ベースの metadata
 * @param {Array} overwrite base を上書きする metadata
 * @returns {Array}
 */
function mergeMetadata(base: Metadata[], overwrite: Metadata[]) {
  return overwrite.reduce((accumulator, current) => {
    if ('name' in current) {
      const updated = accumulator.filter(meta => meta.name !== current.name);
      updated.push(current);
      return updated;
    } else if ('property' in current) {
      const updated = accumulator.filter(
        meta => meta.property !== current.property
      );
      updated.push(current);
      return updated;
    }
    return accumulator;
  }, base);
}

type Link = {
  href: string;
  rel: string;
};

/**
 * mergeLink
 *
 * @param {Array} base ベースの link データ
 * @param {Array} overwrite base を上書きする link データ
 * @returns {Array}
 */
function mergeLink(base: Link[], overwrite: Link[]) {
  return overwrite.reduce((accumulator, current) => {
    const updated = accumulator.filter(link => link.rel !== current.rel);
    updated.push(current);
    return updated;
  }, base);
}

type Props = {
  description?: string | null;
  keywords?: string;
  links?: Link[];
  metas?: Metadata[];
  ogImagePath?: string | null;
  title?: string;
  url?: string;
};

/**
 * HeadChildren
 * @param {Props} props - デフォルトの meta, link 要素を上書きするための配列
 * @return {React.JSX.Element}
 */
export default function GlobalHead(props: Props) {
  const siteMetadata = useSiteMetadata();
  const {description, keywords, ogImagePath, title, url} = props;

  const pageTitle =
    title ||
    'バンドルカード - スマホ1つでチャージ・支払いができて、Visaカードとして使えるアプリ';
  const pageDescription = description || '';
  const pageKeywords = keywords || siteMetadata.keywords;
  const pageUrl = url || siteMetadata.siteUrl;
  const pageImageUrl = `${siteMetadata.siteUrl}${
    ogImagePath || siteMetadata.ogImage
  }`;
  const twitterAccount = `@${siteMetadata.twitterUsername}`;

  const defaultMetas: Metadata[] = [
    {
      name: 'google-site-verification',
      content: siteMetadata.googleSiteVerification,
    },
    {name: 'description', content: pageDescription},
    {name: 'keywords', content: pageKeywords},
    {property: 'fb:app_id', content: siteMetadata.facebookAppId},
    {property: 'og:locale', content: 'ja_JP'},
    {property: 'og:type', content: 'website'},
    {property: 'og:site_name', content: siteMetadata.siteName},
    {property: 'og:url', content: pageUrl},
    {property: 'og:title', content: pageTitle},
    {property: 'og:image', content: pageImageUrl},
    {property: 'og:description', content: pageDescription},
    {name: 'twitter:card', content: 'summary_large_image'},
    {name: 'twitter:site', content: twitterAccount},
    {name: 'twitter:creator', content: twitterAccount},
    {name: 'twitter:title', content: pageTitle},
    {name: 'twitter:text:description', content: pageDescription},
    {name: 'twitter:image', content: pageImageUrl},
  ];

  const defaultLinks: Link[] = [
    {href: url || siteMetadata.siteUrl, rel: 'canonical'},
    {href: `${siteMetadata.siteUrl}/images/favicon.ico`, rel: 'icon'},
  ];

  const metas = !props.metas
    ? defaultMetas
    : mergeMetadata(defaultMetas, props.metas);

  const links = !props.links
    ? defaultLinks
    : mergeLink(defaultLinks, props.links);

  return (
    <>
      <title>{pageTitle}</title>
      {metas.map((item, index) => (
        <meta {...item} key={`meta-${index}`} />
      ))}
      {links.map((link, index) => (
        <link {...link} key={`link-${index}`} />
      ))}
      <script async={true} src="//platform.twitter.com/widgets.js" />
    </>
  );
}
/* eslint-enable */
